import { isUndefined } from 'lodash';

import { ArchivedFilterOptions } from 'common/helpers/utils';
import { FilterData } from 'common/helpers/url';

import { PaginationView } from '../models/paginationModel';

/**
 * Views that must hide archived rows until the user asks for them.
 *
 * Every view listed here is served by an endpoint whose filterset declares
 * `show_archived` (BenefitFieldsFilterSet / ContractFieldsFilterSet), or has the
 * choice renamed to the `archived` its endpoint declares (withArchivedParam), so
 * the default below reaches the API instead of being dropped as an unknown param.
 *
 * Deliberately not listed, because their filtersets expose `archived` rather
 * than `show_archived` and a `showArchived` value is a no-op on the request:
 * EditEmployees (ProfileFilterSet) and the Filters tabs (GenericFilterSet) —
 * both already render the archived dropdown, so their control is inert today
 * and stays inert here.
 */
export const UNARCHIVED_BY_DEFAULT_VIEWS: readonly PaginationView[] = [
    // The six Edit/Download views named in the ticket.
    PaginationView.MyBenefits,
    PaginationView.Benefits,
    PaginationView.DownloadBenefits,
    PaginationView.MyContracts,
    PaginationView.Contracts,
    PaginationView.DownloadContracts,
    // Measure > Benefits. The other four Measure dashboards are served by
    // DashboardViewSet/DashboardFilterSet, which has no `show_archived`, so they
    // cannot be defaulted from the frontend at all.
    PaginationView.BenefitsDashboard,
    // UNCERTAIN(scope): ContractBenefits is NOT one of the six views the ticket
    // names. Included because the Project > Benefits tab already renders this
    // dropdown reading "Unarchived" while listing archived benefits, so leaving
    // it out keeps that control lying in the one place a user can see it, and
    // its endpoint (BenefitViewSet/BenefitFieldsFilterSet) does accept
    // show_archived. Remove this line if the ticket's scope is meant to be
    // literal.
    PaginationView.ContractBenefits,
    // Edit Benefit Templates. SharedBenefitFilterSet has `archived`, not
    // `show_archived`, so withArchivedParam renames the choice for this view.
    // UNCERTAIN(default): this hides archived templates until the user picks
    // Archived or All, which is what the dropdown reads with nothing chosen and
    // what every other view here does. Without this line the page lists both
    // under a dropdown reading "Unarchived"; making the dropdown read "All"
    // instead would need a per-view case in the shared control. Confirm with
    // product which default they want.
    PaginationView.BenefitTemplates,
];

export const defaultsToUnarchived = (view: PaginationView): boolean =>
    UNARCHIVED_BY_DEFAULT_VIEWS.includes(view);

/**
 * Applies the Unarchived default as the request is built rather than seeding it
 * into the store. Seeding would make an untouched view report an active filter,
 * and would turn "Clear Filters" into "show archived too" — which is the bug
 * this fixes, one click away.
 *
 * Only an absent value is defaulted. `ArchivedFilterOptions.All` is a real user
 * choice and is left alone; generateFilterQueryParams maps it to no
 * `show_archived` parameter, which is what "All" means to the API.
 */
export const withUnarchivedDefault = (
    view: PaginationView,
    filters?: FilterData,
): FilterData | undefined => {
    if (!defaultsToUnarchived(view) || (filters && !isUndefined(filters.showArchived))) {
        return filters;
    }

    return { ...filters, showArchived: ArchivedFilterOptions.Unarchived };
};
